import { get } from 'lodash'
import { Container, Grid, Typography } from '@material-ui/core'
import React from 'react'
import { Control, DeepMap, FieldError } from 'react-hook-form'
import { i18n } from '../../../../i18n'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { theme } from '../../../../theme'
import { validators } from '../../../../validation/validators'
import { InputTabFormValues } from './input-util'
import { VentilationInputTabFormValues } from './ventilation-form-values'

type ScheduleComponentProps = {
  control: Control<InputTabFormValues | VentilationInputTabFormValues>
  errors: DeepMap<
    InputTabFormValues | VentilationInputTabFormValues,
    FieldError
  >
  onUpdateModel: (
    values: InputTabFormValues | VentilationInputTabFormValues,
    valueIndex?: number,
  ) => Promise<void>
  scheduleName?: string
  scheduleIndex?: number
}

export function ScheduleHoursRow({
  control,
  errors,
  onUpdateModel,
  scheduleName = 'scheduleValues',
  scheduleIndex = undefined,
}: ScheduleComponentProps): React.ReactElement {
  const scheduleIndexValue =
    scheduleIndex !== undefined ? `.${scheduleIndex.toString()}` : ''
  const scheduleValueName = `${scheduleName}${scheduleIndexValue}`
  const namePrefix = 'hourlyNormals'

  return (
    <Container
      maxWidth={false}
      disableGutters
      style={{ marginTop: theme.spacing(2) }}
    >
      <Typography variant="body2">{i18n.t('common.hours')}</Typography>
      <Grid container spacing={1} style={{ marginTop: theme.spacing(0.5) }}>
        {Array.from(Array(24)).map((_, index) => (
          <Grid
            key={`${scheduleValueName}-hour-${index.toString()}`}
            item
            xs={1}
            lg={1}
            xl
          >
            <ControlledTextField
              key={`${scheduleValueName}-normal-${index.toString()}`}
              control={control}
              label={index.toString()}
              name={`${scheduleValueName}.${namePrefix}.${index}`}
              error={get(errors, `${scheduleValueName}.${namePrefix}.${index}`)} // eslint-disable-line
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              defaultValue="0"
              size="small"
              onBlur={async () => {
                if (scheduleIndex !== undefined)
                  await onUpdateModel(control.getValues(), scheduleIndex)
                else await onUpdateModel(control.getValues())
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Container>
  )
}

export function ScheduleMonthsRow({
  control,
  errors,
  onUpdateModel,
  scheduleName = 'scheduleValues',
  scheduleIndex = undefined,
}: ScheduleComponentProps): React.ReactElement {
  const scheduleIndexValue =
    scheduleIndex !== undefined ? `.${scheduleIndex.toString()}` : ''
  const scheduleValueName = `${scheduleName}${scheduleIndexValue}`
  const namePrefix = 'monthlyValues'

  return (
    <Container
      maxWidth={false}
      disableGutters
      style={{ marginTop: theme.spacing(2) }}
    >
      <Typography variant="body2">{i18n.t('common.months')}</Typography>
      <Grid container spacing={1} style={{ marginTop: theme.spacing(0.5) }}>
        {Array.from(Array(12)).map((_, index) => (
          <Grid key={`${scheduleValueName}-month-${index.toString()}`} item xs>
            <ControlledTextField
              key={`${scheduleValueName}-normal-${index.toString()}`}
              control={control}
              label={i18n.t(`date.months.${index + 1}`)}
              name={`${scheduleValueName}.${namePrefix}.${index}`}
              error={get(errors, `${scheduleValueName}.${namePrefix}.${index}`)} // eslint-disable-line
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              defaultValue="0"
              validateOnBlur
              size="small"
              onBlur={async () => {
                if (scheduleIndex !== undefined)
                  await onUpdateModel(control.getValues(), scheduleIndex)
                else await onUpdateModel(control.getValues())
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Container>
  )
}
